import { useMemo, useState, useEffect } from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { getFormatPrefs } from "./formatPrefs";
import { useI18n } from "../i18n";
import { useUnits } from "../context/UnitsContext";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const LANGUAGE_FORMAT = {
  en: { locale: "en-US" },
  es: { locale: "es-ES" },
};

const SUPPORTED_CURRENCIES = new Set(["USD", "AUD", "COP", "EUR", "CAD"]);

function getFormatConfig(overrides = {}) {
  const lang = overrides.language ?? getFormatPrefs().language ?? "en";
  return LANGUAGE_FORMAT[lang] || LANGUAGE_FORMAT.en;
}

function getPreferredCurrency(overrides = {}) {
  const curr = overrides.currency ?? getFormatPrefs().currency ?? "USD";
  return SUPPORTED_CURRENCIES.has(curr) ? curr : "USD";
}

export function getCurrencyForLoanCountry(countryCode) {
  const c = (countryCode || 'US').toString().toUpperCase().slice(0, 2);
  // Only return currencies in SUPPORTED_CURRENCIES; unsupported codes fall back to USD
  const map = {
    US: 'USD',
    AU: 'AUD',
    CO: 'COP',
    ES: 'EUR',
    DE: 'EUR',
    FR: 'EUR',
    CA: 'CAD',
    // GB → GBP is not in the exchange-rate service; fall through to USD default
  };
  return map[c] || 'USD';
}

function getCurrencyFractionDigits(currency) {
  if (currency === "COP") {
    return { minimumFractionDigits: 0, maximumFractionDigits: 0 };
  }
  return { minimumFractionDigits: 2, maximumFractionDigits: 2 };
}

function toSafeNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Resolve fraction digits for Intl (min must be <= max, both 0–20). */
function resolveFractionDigits(options = {}) {
  const minOpt = options.minimumFractionDigits;
  const maxOpt = options.maximumFractionDigits;
  let minimumFractionDigits;
  let maximumFractionDigits;

  if (maxOpt !== undefined && minOpt === undefined) {
    minimumFractionDigits = 0;
    maximumFractionDigits = maxOpt;
  } else if (minOpt !== undefined && maxOpt === undefined) {
    minimumFractionDigits = minOpt;
    maximumFractionDigits = minOpt;
  } else {
    minimumFractionDigits = minOpt ?? 2;
    maximumFractionDigits = maxOpt ?? minimumFractionDigits;
  }

  minimumFractionDigits = Math.max(0, Math.min(20, minimumFractionDigits));
  maximumFractionDigits = Math.max(0, Math.min(20, maximumFractionDigits));
  if (minimumFractionDigits > maximumFractionDigits) {
    minimumFractionDigits = maximumFractionDigits;
  }

  return { minimumFractionDigits, maximumFractionDigits };
}

function stripFormatMeta(options = {}) {
  const {
    language: _language,
    currency: _currency,
    fromRatio: _fromRatio,
    minimumFractionDigits: _min,
    maximumFractionDigits: _max,
    ...rest
  } = options;
  return rest;
}

/** True when viewport is below Tailwind `sm` (640px). */
export function useNarrowViewport(breakpointPx = 639) {
  const query = `(max-width: ${breakpointPx}px)`;
  const [narrow, setNarrow] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setNarrow(e.matches);
    onChange(mq);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return narrow;
}

/** React hook: formatters that re-render when language or display currency changes. */
export function useFormatting() {
  const { language } = useI18n();
  const { currency } = useUnits();
  const narrow = useNarrowViewport();
  return useMemo(
    () => ({
      language,
      currency,
      narrow,
      formatCurrency: (amount, opts = {}) =>
        formatCurrency(amount, { ...opts, currency: opts.currency || currency, language }),
      formatCompactCurrency: (amount, opts = {}) =>
        formatCompactCurrency(amount, { ...opts, currency: opts.currency || currency, language }),
      formatChartAxisCurrency: (amount, opts = {}) =>
        formatChartAxisCurrency(amount, { ...opts, currency: opts.currency || currency, language }),
      formatResponsiveCurrency: (amount, opts = {}) =>
        formatResponsiveCurrency(amount, {
          ...opts,
          currency: opts.currency || currency,
          language,
          isNarrow: opts.isNarrow ?? narrow,
        }),
      formatStatCurrency: (amount, opts = {}) =>
        formatStatCurrency(amount, {
          ...opts,
          currency: opts.currency || currency,
          language,
        }),
      formatPercent: (value, opts = {}) => formatPercent(value, { ...opts, language }),
      formatNumber: (value, opts = {}) => formatNumber(value, opts),
      formatDate: (dateString, opts = {}) => formatDate(dateString, opts),
    }),
    [language, currency, narrow]
  );
}

export function formatNumber(value, options = {}) {
  const { locale } = getFormatConfig(options);
  const fractionDigits = resolveFractionDigits(options);
  return new Intl.NumberFormat(locale, {
    ...fractionDigits,
    ...stripFormatMeta(options),
  }).format(toSafeNumber(value));
}

// Format currency (supports legacy formatCurrency(amount, currency))
export function formatCurrency(amount, options = {}) {
  const legacyOptions = typeof options === "string" ? { currency: options } : options;
  const { locale } = getFormatConfig(legacyOptions);
  const defaultCurrency = getPreferredCurrency(legacyOptions);
  const currency = legacyOptions.currency || defaultCurrency;
  const defaultFractionDigits = getCurrencyFractionDigits(currency);
  const fractionDigits = resolveFractionDigits({
    minimumFractionDigits:
      legacyOptions.minimumFractionDigits ?? defaultFractionDigits.minimumFractionDigits,
    maximumFractionDigits:
      legacyOptions.maximumFractionDigits ?? defaultFractionDigits.maximumFractionDigits,
  });

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    ...fractionDigits,
    ...stripFormatMeta(legacyOptions),
  }).format(toSafeNumber(amount));
}

/** Short currency for charts and narrow layouts (e.g. $1.2M, 45 mil. €). */
export function formatCompactCurrency(amount, options = {}) {
  const legacyOptions = typeof options === "string" ? { currency: options } : options;
  const { locale } = getFormatConfig(legacyOptions);
  const currency = legacyOptions.currency || getPreferredCurrency(legacyOptions);
  const n = toSafeNumber(amount);
  const maxFrac = currency === "COP" ? 0 : 1;
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      notation: "compact",
      compactDisplay: "short",
      maximumFractionDigits: maxFrac,
      ...stripFormatMeta(legacyOptions),
    }).format(n);
  } catch {
    const abs = Math.abs(n);
    if (abs >= 1_000_000) {
      return `${formatCurrency(n / 1_000_000, { ...legacyOptions, maximumFractionDigits: 1 })}M`;
    }
    if (abs >= 1_000) {
      return `${formatCurrency(n / 1_000, { ...legacyOptions, maximumFractionDigits: 1 })}K`;
    }
    return formatCurrency(n, legacyOptions);
  }
}

/** Chart Y-axis ticks — full value, compact notation (no manual /1000). */
export function formatChartAxisCurrency(value, options = {}) {
  return formatCompactCurrency(value, options);
}

/** Full currency on desktop; compact when narrow and amount is large. */
export function formatResponsiveCurrency(amount, options = {}) {
  const { isNarrow = false, compactThreshold = 10_000, ...rest } = options;
  const n = toSafeNumber(amount);
  if (isNarrow && Math.abs(n) >= compactThreshold) {
    return formatCompactCurrency(n, rest);
  }
  return formatCurrency(n, rest);
}

const STAT_CARD_COMPACT_THRESHOLD = {
  COP: 1_000,
  USD: 50_000,
  AUD: 50_000,
  EUR: 50_000,
  CAD: 50_000,
};

/**
 * Stat cards / KPI tiles — compact notation when values are long (esp. COP in a 4-col grid).
 * Pass forceFull: true for hover titles.
 */
export function formatStatCurrency(amount, options = {}) {
  const legacyOptions = typeof options === "string" ? { currency: options } : options;
  const { forceFull = false, ...rest } = legacyOptions;
  const n = toSafeNumber(amount);
  const currency = rest.currency || getPreferredCurrency(rest);
  const threshold =
    rest.compactThreshold ?? STAT_CARD_COMPACT_THRESHOLD[currency] ?? 50_000;

  if (forceFull) {
    return formatCurrency(n, rest);
  }
  if (Math.abs(n) >= threshold) {
    return formatCompactCurrency(n, rest);
  }
  return formatCurrency(n, rest);
}

export function formatPercent(value, options = {}) {
  const { locale } = getFormatConfig(options);
  const { fromRatio = false } = options;
  const fractionDigits = resolveFractionDigits(options);
  const normalized = fromRatio ? toSafeNumber(value) : toSafeNumber(value) / 100;
  return new Intl.NumberFormat(locale, {
    style: "percent",
    ...fractionDigits,
    ...stripFormatMeta(options),
  }).format(normalized);
}

// Format date
export function formatDate(dateString, options = {}) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const { locale } = getFormatConfig(options);
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...options,
  });
}

// Format relative date — locale-aware using Intl.RelativeTimeFormat
export function formatRelativeDate(dateString, options = {}) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = date - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const { locale } = getFormatConfig(options);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    if (Math.abs(diffDays) <= 7) {
      return rtf.format(diffDays, "day");
    }
    if (Math.abs(diffDays) <= 30) {
      return rtf.format(Math.round(diffDays / 7), "week");
    }
  } catch {
    // Fallback for environments where Intl.RelativeTimeFormat is unavailable
  }
  return formatDate(dateString, options);
}

// Get status badge classes
export function getStatusBadgeClass(status) {
  const statusClasses = {
    active: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
    completed: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
    paid: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
    pending: 'bg-amber-50 text-amber-700 border border-amber-100',
    scheduled: 'bg-blue-50 text-blue-700 border border-blue-100',
    in_progress: 'bg-blue-50 text-blue-700 border border-blue-100',
    approved: 'bg-blue-50 text-blue-700 border border-blue-100',
    expired: 'bg-red-50 text-red-700 border border-red-100',
    terminated: 'bg-red-50 text-red-700 border border-red-100',
    cancelled: 'bg-slate-50 text-slate-700 border border-slate-100',
    vacant: 'bg-slate-50 text-slate-700 border border-slate-100',
    maintenance: 'bg-amber-50 text-amber-700 border border-amber-100',
  };
  return statusClasses[status] || 'bg-slate-50 text-slate-700 border border-slate-100';
}

// Get priority badge class
export function getPriorityBadgeClass(priority) {
  const priorityClasses = {
    low: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
    medium: 'bg-amber-50 text-amber-700 border border-amber-100',
    high: 'bg-red-50 text-red-700 border border-red-100',
    urgent: 'bg-purple-50 text-purple-700 border border-purple-100',
  };
  return priorityClasses[priority] || 'bg-slate-50 text-slate-700 border border-slate-100';
}

// Capitalize first letter
export function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).replace(/_/g, ' ');
}

// Calculate percentage change
export function calculatePercentageChange(current, previous) {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Number((((current - previous) / previous) * 100).toFixed(2));
}

// Truncate text
export function truncateText(text, maxLength = 50) {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}
